import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ContractSignaturePad } from "@/components/ContractSignaturePad";
import { AchBankInfoStep } from "@/components/AchBankInfoStep";
import PdfPreview from "@/components/PdfPreview";
import {
    ShieldCheck,
    FileSignature,
    AlertTriangle,
    CheckCircle2,
    FileText,
    PenLine,
    Banknote,
} from "lucide-react";

import { getBackendBase } from "@/lib/api";
const BACKEND_URL = getBackendBase();  // Phase APEX-C — same-origin only. See lib/api.js.

/**
 * Phase E1 redesign — 3-step ESIGN/UETA-grade signing flow:
 *  1. Review  — embedded PDF preview
 *  2. Consent — typed full legal name + checkbox + ESIGN/UETA paragraph
 *  3. Sign    — signature pad with restated name + final retype-to-confirm
 */
export default function SignContractPage() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const prefillName = (searchParams.get("name") || "").trim();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    // Phase N.7 — structured error code from the token-fetch response
    // so the error UI can branch on ``invalid`` / ``revoked`` /
    // ``expired`` and offer a "Request new link" CTA for the expired
    // case.
    const [errorCode, setErrorCode] = useState(null);
    const [resendBusy, setResendBusy] = useState(false);
    const [resendSent, setResendSent] = useState(false);
    // Phase N.7.1 — dedicated inline slot for resend failures
    // (rate-limit / network) so we don't overwrite the primary
    // ``This signing link has expired.`` message on the error card.
    const [resendError, setResendError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [done, setDone] = useState(null);
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState(1);
    const [consentName, setConsentName] = useState(prefillName);
    const [consentChecked, setConsentChecked] = useState(false);
    const [reconfirmName, setReconfirmName] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND_URL}/api/sign/contract/${token}`);
                setInfo(data);
                if (data.consent_recorded && data.consent_record?.signer_typed_full_name) {
                    setConsentName(data.consent_record.signer_typed_full_name);
                    setStep(3);
                } else if (prefillName && !consentName) {
                    setConsentName(prefillName);
                }
            } catch (err) {
                // Phase N.7 — token-fetch errors are now structured:
                // ``{detail: {code, message, ...}}``. Fall back to the
                // legacy string shape for older backends / other
                // failures.
                const d = err?.response?.data?.detail;
                if (d && typeof d === "object") {
                    setError(d.message || "Invalid link");
                    setErrorCode(d.code || null);
                } else {
                    setError(d || err?.message || "Invalid link");
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const onConsent = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${BACKEND_URL}/api/sign/contract/${token}/consent`, {
                signer_typed_full_name: consentName.trim(),
                consent_text_version: info?.consent_text_version || "v1",
                agreed: true,
            });
            setInfo({ ...info, consent_recorded: true, consent_record: data.consent_record });
            setStep(3);
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not record consent");
        } finally {
            setBusy(false);
        }
    };

    const onSign = async (payload) => {
        if (reconfirmName.trim().toLowerCase() !== consentName.trim().toLowerCase()) {
            setError("Please re-type your full legal name exactly as on the consent step.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${BACKEND_URL}/api/sign/contract/${token}/signature`, {
                ...payload,
                signer_typed_full_name: consentName.trim(),
            });
            // Phase F2 — if a bundled ACH document is still pending, advance into
            // the bank-info / ACH-signature flow. Otherwise show the success card.
            if (data.ach_pending) {
                setReconfirmName("");
                setStep(4);
                // Refresh bundle state so we know the ACH doc id
                try {
                    const refreshed = await axios.get(`${BACKEND_URL}/api/sign/contract/${token}`);
                    setInfo(refreshed.data);
                } catch { /* ignore */ }
            } else {
                setDone(data);
            }
        } catch (err) {
            setError(err?.response?.data?.detail || "Signature failed");
        } finally {
            setBusy(false);
        }
    };

    const achDoc = (info?.documents || []).find((d) => d.doc_type === "ach_authorization") || null;
    const achHasBankInfo = Boolean(achDoc?.ach_bank_info);

    const onAchBankInfoCaptured = async () => {
        try {
            const refreshed = await axios.get(`${BACKEND_URL}/api/sign/contract/${token}`);
            setInfo(refreshed.data);
        } catch { /* ignore */ }
        setStep(5);
    };

    const onAchSign = async (payload) => {
        if (reconfirmName.trim().toLowerCase() !== consentName.trim().toLowerCase()) {
            setError("Please re-type your full legal name exactly as on the consent step.");
            return;
        }
        if (!achDoc?.doc_id) {
            setError("Could not locate ACH document on this contract.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${BACKEND_URL}/api/sign/contract/${token}/signature`,
                { ...payload, signer_typed_full_name: consentName.trim() },
                { params: { doc_id: achDoc.doc_id } },
            );
            setDone({ ...data, doc_type: "ach_authorization" });
        } catch (err) {
            setError(err?.response?.data?.detail || "ACH signature failed");
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <FullScreen><p className="text-sm text-slate-500">Loading contract…</p></FullScreen>;

    // Phase N.7.1 — success card wins over ALL error branches. Once the
    // public resend endpoint has rotated the token and dispatched the
    // email, this screen becomes the terminal state. We never fall
    // through to the error UI again.
    if (resendSent) {
        return (
            <FullScreen>
                <div
                    className="rounded-md border border-emerald-200 bg-emerald-50 p-6 max-w-md text-center"
                    data-testid="sign-resend-success"
                >
                    <CheckCircle2 size={28} className="mx-auto text-emerald-700 mb-3" />
                    <p className="font-semibold text-emerald-900">Fresh signing link sent</p>
                    <p className="text-xs text-emerald-800 mt-2">
                        Check the email address on file — a new signing link has
                        been sent and expires in 7 days. This tab is safe to close.
                    </p>
                    <a
                        href="/customer/dashboard"
                        className="mt-4 inline-block text-xs font-medium text-emerald-900 underline hover:no-underline"
                        data-testid="sign-resend-return-to-portal"
                    >
                        Return to portal
                    </a>
                </div>
            </FullScreen>
        );
    }

    if (error && !info) {
        // Phase N.7 — branch by structured code:
        //  * ``expired`` → show "Request new signing link" CTA (fires
        //    the public endpoint with the stale token from the URL).
        //  * ``revoked`` → terminal, contract already signed / cancelled.
        //  * ``invalid`` → terminal, bad token.
        const isExpired = errorCode === "expired";
        const isRevoked = errorCode === "revoked";
        const requestNewLink = async () => {
            setResendBusy(true);
            setResendError(null);
            try {
                await axios.post(
                    `${BACKEND_URL}/api/public/contracts/resend-signing-link`,
                    { token },
                );
                // Phase N.7.1 — do NOT overwrite ``error``/``errorCode``
                // or retrigger any fetch here. Flipping ``resendSent``
                // routes rendering to the dedicated success branch
                // above; the token-fetch effect will not re-fire
                // because ``token`` (the effect's only dep) hasn't
                // changed.
                setResendSent(true);
            } catch (err) {
                const d = err?.response?.data?.detail;
                if (d && typeof d === "object" && d.code === "rate_limited") {
                    const mins = Math.max(1, Math.round((d.retry_after || 3600) / 60));
                    setResendError(`Too many resends recently. Try again in ~${mins} minute${mins === 1 ? "" : "s"}.`);
                } else if (d && typeof d === "object") {
                    setResendError(d.message || "Could not send a new link.");
                } else {
                    setResendError(typeof d === "string" ? d : (err?.message || "Could not send a new link."));
                }
            } finally {
                setResendBusy(false);
            }
        };
        return (
            <FullScreen>
                <div
                    className="rounded-md p-6 max-w-md text-center border bg-red-50 border-red-200"
                    data-testid="sign-error"
                    data-error-code={errorCode || ""}
                >
                    <AlertTriangle size={28} className="mx-auto text-red-700 mb-3" />
                    <p className="font-semibold text-red-900">
                        {isExpired && "This signing link has expired."}
                        {isRevoked && "This link is no longer valid."}
                        {!isExpired && !isRevoked && error}
                    </p>
                    {isExpired && (
                        <>
                            <p className="text-xs text-red-700 mt-2">
                                Request a fresh one — we'll email it to the address on file.
                            </p>
                            <button
                                type="button"
                                onClick={requestNewLink}
                                disabled={resendBusy}
                                className="mt-4 inline-flex items-center gap-2 rounded-md bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
                                data-testid="sign-request-new-link-btn"
                            >
                                {resendBusy ? "Sending…" : "Request new signing link"}
                            </button>
                            {resendError && (
                                <p className="mt-3 text-xs text-red-800" data-testid="sign-resend-error">
                                    {resendError}
                                </p>
                            )}
                        </>
                    )}
                    {isRevoked && (
                        <p className="text-xs text-red-700 mt-2">
                            The contract may have already been signed or canceled. Contact the company that sent you this link.
                        </p>
                    )}
                    {!isExpired && !isRevoked && (
                        <p className="text-xs text-red-700 mt-2">
                            If you believe this is a mistake, contact the company that sent you this link.
                        </p>
                    )}
                </div>
            </FullScreen>
        );
    }

    if (done) {
        return (
            <FullScreen>
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-8 max-w-md text-center" data-testid="sign-success">
                    <CheckCircle2 size={32} className="mx-auto text-emerald-700 mb-3" />
                    <h1 className="font-display text-2xl font-bold text-slate-900">Thanks — your signature is on file</h1>
                    <p className="text-sm text-slate-700 mt-3">
                        Your contract is now being countersigned by <strong>{done.company_name || "the company"}</strong>.
                        You'll receive a copy of the fully-signed contract once that's complete.
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-4">Signed at {new Date(done.signed_at).toLocaleString()}</p>
                </div>
            </FullScreen>
        );
    }

    const pdfUrl = `${BACKEND_URL}/api/sign/contract/${token}/pdf`;

    return (
        <div className="min-h-screen bg-slate-50 py-10 px-4">
            <div className="max-w-5xl mx-auto space-y-6">
                <header className="flex items-start gap-3">
                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-blue-900 text-white">
                        <FileSignature size={20} />
                    </span>
                    <div className="min-w-0">
                        <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">CONTRACT FOR SIGNATURE</p>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="sign-title">{info.title}</h1>
                        <p className="text-sm text-slate-600 mt-1">
                            From <strong>{info.company_name}</strong>
                            {info.billing_contact_name && <span> · for {info.billing_contact_name}</span>}
                        </p>
                    </div>
                </header>

                <Stepper step={step} hasAch={Boolean(achDoc)} />

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex items-start gap-2" data-testid="sign-inline-error">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                        <span>{error}</span>
                        <button className="ml-auto text-xs underline" onClick={() => setError(null)}>Dismiss</button>
                    </div>
                )}

                {step === 1 && (
                    <ReviewStep info={info} pdfUrl={pdfUrl} onContinue={() => setStep(2)} />
                )}
                {step === 2 && (
                    <ConsentStep
                        info={info}
                        consentName={consentName}
                        setConsentName={setConsentName}
                        consentChecked={consentChecked}
                        setConsentChecked={setConsentChecked}
                        prefilled={Boolean(prefillName)}
                        onBack={() => setStep(1)}
                        onContinue={onConsent}
                        busy={busy}
                    />
                )}
                {step === 3 && (
                    <SignStep
                        info={info}
                        pdfUrl={pdfUrl}
                        consentName={consentName}
                        reconfirmName={reconfirmName}
                        setReconfirmName={setReconfirmName}
                        onBack={() => setStep(2)}
                        onSign={onSign}
                        busy={busy}
                    />
                )}
                {step === 4 && achDoc && (
                    <AchBankInfoStep
                        token={token}
                        achDoc={achDoc}
                        defaultHolderName={info?.billing_contact_name || consentName}
                        onBack={() => setStep(3)}
                        onCaptured={onAchBankInfoCaptured}
                    />
                )}
                {step === 5 && achDoc && (
                    <AchSignStep
                        info={info}
                        achDoc={achDoc}
                        consentName={consentName}
                        reconfirmName={reconfirmName}
                        setReconfirmName={setReconfirmName}
                        onBack={() => setStep(4)}
                        onSign={onAchSign}
                        busy={busy}
                        bankInfoCaptured={achHasBankInfo}
                    />
                )}
            </div>
        </div>
    );
}

function Stepper({ step, hasAch }) {
    const items = hasAch
        ? [
            { n: 1, label: "Review", icon: FileText },
            { n: 2, label: "Consent", icon: ShieldCheck },
            { n: 3, label: "Sign rental", icon: PenLine },
            { n: 4, label: "Bank info", icon: Banknote },
            { n: 5, label: "Sign ACH", icon: PenLine },
        ]
        : [
            { n: 1, label: "Review", icon: FileText },
            { n: 2, label: "Consent", icon: ShieldCheck },
            { n: 3, label: "Sign", icon: PenLine },
        ];
    return (
        <ol className="flex items-center gap-3 sm:gap-6 text-xs sm:text-sm" data-testid="sign-stepper">
            {items.map((it, idx) => {
                const Icon = it.icon;
                const active = it.n === step;
                const done = it.n < step;
                return (
                    <React.Fragment key={it.n}>
                        <li className={`flex items-center gap-2 ${active ? "text-blue-900" : done ? "text-emerald-700" : "text-slate-400"}`}
                            data-testid={`sign-step-${it.n}`}>
                            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ring-1 ${
                                active ? "bg-blue-900 text-white ring-blue-900" :
                                done ? "bg-emerald-100 text-emerald-700 ring-emerald-300" :
                                "bg-white text-slate-400 ring-slate-200"
                            }`}>
                                {done ? <CheckCircle2 size={14} /> : <Icon size={13} />}
                            </span>
                            <span className="font-semibold tracking-wide uppercase">{it.label}</span>
                        </li>
                        {idx < items.length - 1 && <span aria-hidden="true" className="h-px flex-1 bg-slate-200" />}
                    </React.Fragment>
                );
            })}
        </ol>
    );
}

function AchSignStep({ info, achDoc, consentName, reconfirmName, setReconfirmName, onBack, onSign, busy, bankInfoCaptured }) {
    return (
        <>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900" data-testid="ach-sign-banner">
                Signing the <strong>ACH Authorization</strong> as <strong data-testid="ach-sign-typed-name">{consentName}</strong>.
                {bankInfoCaptured && achDoc?.ach_bank_info && (
                    <span className="block mt-1 text-blue-800">
                        We'll debit <strong>{achDoc.ach_bank_info.bank_name}</strong> · {achDoc.ach_bank_info.account_type} ··•• <strong>{achDoc.ach_bank_info.account_number_last4}</strong>.
                    </span>
                )}
            </div>
            <Card>
                <CardContent className="p-6 space-y-4" data-testid="ach-sign-step">
                    <h2 className="font-semibold text-slate-900">Your ACH signature</h2>
                    <ContractSignaturePad
                        defaultSignerName={consentName || info.billing_contact_name || ""}
                        onSign={(payload) => onSign({ ...payload, signer_typed_full_name: consentName })}
                        busy={busy}
                    />
                    <div className="space-y-1.5">
                        <Label htmlFor="ach-sign-reconfirm">Re-type your full legal name to confirm <span className="text-red-500">*</span></Label>
                        <Input id="ach-sign-reconfirm" value={reconfirmName}
                            onChange={(e) => setReconfirmName(e.target.value)}
                            placeholder={consentName} data-testid="ach-sign-reconfirm" />
                        <p className="text-[11px] text-slate-500">This must match exactly the name you typed during the consent step.</p>
                    </div>
                    <div className="flex justify-between gap-3 pt-2 border-t border-slate-100">
                        <Button variant="outline" onClick={onBack} data-testid="ach-sign-back">← Back</Button>
                    </div>
                </CardContent>
            </Card>
        </>
    );
}

function ReviewStep({ info, pdfUrl, onContinue }) {
    return (
        <>
            <div className="rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="sign-banner">
                <ShieldCheck size={18} className="shrink-0 mt-0.5 text-amber-800" />
                <div className="text-sm text-amber-900">
                    <p className="font-semibold">You are about to electronically sign a contract.</p>
                    <p className="mt-1">Read the document below carefully. The next two steps will record your consent under the ESIGN Act / UETA and capture your signature.</p>
                </div>
            </div>
            <Card>
                <CardContent className="p-0">
                    <PdfPreview
                        src={pdfUrl}
                        downloadHref={pdfUrl}
                        downloadName="contract.pdf"
                        testid="sign-pdf"
                    />
                </CardContent>
            </Card>
            <div className="flex justify-end">
                <Button onClick={onContinue} className="bg-blue-900 hover:bg-blue-950" data-testid="sign-review-continue">
                    Continue to consent →
                </Button>
            </div>
        </>
    );
}

function ConsentStep({ info, consentName, setConsentName, consentChecked, setConsentChecked, prefilled, onBack, onContinue, busy }) {
    const ready = consentName.trim().length >= 2 && consentChecked;
    return (
        <Card>
            <CardContent className="p-6 space-y-5" data-testid="sign-consent-step">
                <div>
                    <h2 className="font-display text-2xl font-bold text-slate-900">Electronic consent</h2>
                    <p className="text-sm text-slate-600 mt-1">Required by the ESIGN Act (15 U.S.C. § 7001) and the Uniform Electronic Transactions Act.</p>
                </div>
                <pre className="whitespace-pre-wrap text-sm leading-6 text-slate-800 bg-slate-50 border border-slate-200 rounded-md p-4 font-sans"
                    data-testid="sign-consent-text">{info.consent_text}</pre>
                <div className="space-y-2">
                    <Label htmlFor="sign-consent-name">Type your full legal name as it appears on the contract <span className="text-red-500">*</span></Label>
                    <Input
                        id="sign-consent-name"
                        value={consentName}
                        onChange={(e) => setConsentName(e.target.value)}
                        placeholder="e.g. Jane Q. Public"
                        data-testid="sign-consent-name"
                    />
                    {prefilled && (
                        <p className="text-[11px] text-slate-500" data-testid="sign-consent-prefill-helper">
                            We pre-filled your name from the contract — please confirm it matches your legal name.
                        </p>
                    )}
                </div>
                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="sign-consent-checkbox-label">
                    <Checkbox
                        id="sign-consent-checkbox"
                        checked={consentChecked}
                        onCheckedChange={(v) => setConsentChecked(Boolean(v))}
                        data-testid="sign-consent-checkbox"
                        className="mt-0.5"
                    />
                    <span className="text-sm text-slate-800">
                        I have read and agree to the above and intend to sign this contract electronically.
                    </span>
                </label>
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
                    <Button variant="outline" onClick={onBack} data-testid="sign-consent-back">← Back</Button>
                    <Button
                        onClick={onContinue}
                        disabled={!ready || busy}
                        className="bg-blue-900 hover:bg-blue-950"
                        data-testid="sign-consent-continue">
                        {busy ? "Recording…" : "Continue to sign →"}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

function SignStep({ info, pdfUrl, consentName, reconfirmName, setReconfirmName, onBack, onSign, busy }) {
    return (
        <>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900" data-testid="sign-step-banner">
                Signing as <strong data-testid="sign-step-typed-name">{consentName}</strong>. Use the pad below to draw or type your signature, re-type your full name, then click <em>I sign this contract</em>.
            </div>
            <Card>
                <CardContent className="p-6 space-y-4">
                    <h2 className="font-semibold text-slate-900">Your signature</h2>
                    <ContractSignaturePad
                        defaultSignerName={consentName || info.billing_contact_name || ""}
                        onSign={(payload) => onSign({ ...payload, signer_typed_full_name: consentName })}
                        busy={busy}
                        ctaLabel="I sign this contract"
                        testIdPrefix="sign"
                        extraFields={(
                            <div className="space-y-1.5 mt-2">
                                <Label htmlFor="sign-reconfirm-name">Re-type your full legal name to confirm <span className="text-red-500">*</span></Label>
                                <Input
                                    id="sign-reconfirm-name"
                                    value={reconfirmName}
                                    onChange={(e) => setReconfirmName(e.target.value)}
                                    placeholder={consentName}
                                    data-testid="sign-reconfirm-name"
                                />
                                <p className="text-[11px] text-slate-500">
                                    Must exactly match: <strong>{consentName}</strong>
                                </p>
                            </div>
                        )}
                        canSubmit={reconfirmName.trim().toLowerCase() === (consentName || "").trim().toLowerCase() && reconfirmName.trim().length >= 2}
                    />
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <Button variant="outline" onClick={onBack} data-testid="sign-step-back">← Back to consent</Button>
                        <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-900 hover:underline" data-testid="sign-step-view-pdf">
                            View contract PDF in a new tab
                        </a>
                    </div>
                </CardContent>
            </Card>
        </>
    );
}

function FullScreen({ children }) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-10">{children}</div>;
}
